import React, { createContext, useContext, useState, useEffect } from 'react';

type ConsentStatus = 'unknown' | 'accepted' | 'refused' | 'custom';

interface CookieContextType {
    consentStatus: ConsentStatus;
    setConsentStatus: (status: ConsentStatus) => void;
    showBanner: boolean;
    setShowBanner: (show: boolean) => void;
    resetConsent: () => void;
    optOut: boolean;
    toggleOptOut: () => void;
}

const CookieContext = createContext<CookieContextType | undefined>(undefined);

export const CookieProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    // Synchronous initialization to prevent "flash" of unblocked content
    const [consentStatus, setConsentStatusState] = useState<ConsentStatus>(() => {
        if (typeof window === 'undefined') return 'unknown';
        try {
            const storedConsent = localStorage.getItem('cookieconfig');
            if (storedConsent) {
                const parsed = JSON.parse(storedConsent);

                // Check for expiration (13 months)
                const CONSENT_DURATION_MS = 13 * 30 * 24 * 60 * 60 * 1000; // ~13 months
                const consentDate = new Date(parsed.timestamp).getTime();
                const now = new Date().getTime();

                // If expired or invalid date, reset to unknown (shows banner again)
                if (!consentDate || (now - consentDate > CONSENT_DURATION_MS)) {
                    // We return 'unknown' to trigger the banner, but we KEPT the data in localStorage
                    // thanks to the fix in apiClient.js
                    return 'unknown';
                }

                if (['accepted', 'refused', 'custom'].includes(parsed.status)) {
                    return parsed.status;
                }
            }
        } catch (e) {
            // ignore
        }
        return 'unknown';
    });

    const [showBanner, setShowBanner] = useState(consentStatus === 'unknown');


    // Rolling Consent Expiration: Update timestamp on every visit if consent is already given
    useEffect(() => {
        if (consentStatus === 'accepted' || consentStatus === 'custom') {
            try {
                const stored = localStorage.getItem('cookieconfig');
                if (stored) {
                    const parsed = JSON.parse(stored);
                    // Update timestamp to NOW to extend validity (Rolling 13 months)
                    // This aligns with "Contract/Service" basis: continued use extends the session/preferences.
                    localStorage.setItem('cookieconfig', JSON.stringify({
                        ...parsed,
                        timestamp: new Date().toISOString()
                    }));
                }
            } catch (e) {
                // ignore
            }
        }
    }, [consentStatus]);

    // Opt-out logic for Visitor Analytics
    const [optOut, setOptOut] = useState(() => {
        if (typeof window === 'undefined') return false;
        return localStorage.getItem('visitor_opt_out') === 'true';
    });

    const toggleOptOut = () => {
        setOptOut(prev => {
            const newValue = !prev;
            localStorage.setItem('visitor_opt_out', String(newValue));
            return newValue;
        });
    };

    const setConsentStatus = (status: ConsentStatus) => {
        setConsentStatusState(status);
        localStorage.setItem('cookieconfig', JSON.stringify({
            status,
            timestamp: new Date().toISOString()
        }));

        if (status === 'refused') {
            localStorage.setItem('visitor_opt_out', 'true');
            setOptOut(true);
        } else if (status === 'accepted') {
            localStorage.removeItem('visitor_opt_out');
            setOptOut(false);
        }

        setShowBanner(false);
    };

    const resetConsent = () => {
        setConsentStatusState('unknown');
        localStorage.removeItem('cookieconfig');
        setShowBanner(true);
    };


    return (
        <CookieContext.Provider value={{ consentStatus, setConsentStatus, showBanner, setShowBanner, resetConsent, optOut, toggleOptOut }}>
            {children}
        </CookieContext.Provider>
    );
};

export const useCookie = () => {
    const context = useContext(CookieContext);
    if (!context) {
        throw new Error('useCookie must be used within a CookieProvider');
    }
    return context;
};
